import {Component, computed, effect, inject, viewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {PageGridComponent} from '../../../../shared/components/page-grid/page-grid.component';
import {TmtErpPanelFilterService} from '../../service/tmt-erp-panel-filter.service';
import {TmtErpReportGridService} from '../../service/tmt-erp-report-grid.service';
import {GridEventOutput, GridModel} from '../../../../shared/model/page-grid';
import {ITmtErpReportItem} from '../../interfaces/tmt-erp-panel.interface';
import {DatePipe} from '@angular/common';
import {environment} from '../../../../../environments/environment';
import {AdxErpPayloadDialogComponent} from '../../../adx-erp-panel/components/adx-erp-payload-dialog/adx-erp-payload-dialog.component';
import {TmtErpTimelineDialogComponent} from '../tmt-erp-timeline-dialog/tmt-erp-timeline-dialog.component';
import {
  TmtErpManualUpdateDialogComponent,
} from '../tmt-erp-manual-update-dialog/tmt-erp-manual-update-dialog.component';

@Component({
  selector: 'app-tmt-erp-report-list',
  templateUrl: './tmt-erp-report-list.component.html',
  standalone: false,
})
export class TmtErpReportListComponent {
  private readonly filterService = inject(TmtErpPanelFilterService);
  private readonly gridService = inject(TmtErpReportGridService);
  private readonly datePipe = inject(DatePipe);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);

  protected readonly gridRef = viewChild.required(PageGridComponent);

  protected readonly gridConfig = computed<GridModel<ITmtErpReportItem>>(() => ({
    gridDataTestId: 'tmt-erp-report-list',
    gridTitle: 'TMT → ERP Invoice Report',
    defaultPageSize: 25,
    replayButton: true,
    // Manual ERP close-out writes directly to sync/response state, so — same as QA Playground —
    // it's gated off in production and only available in non-prod environments.
    showActionColumn: environment.enablePlayground,
    actionColumnHeader: 'Manual Update',
    columns: [
      {
        columnProperty: 'tmtSalesOrderId',
        columnHeader: 'Sales Order ID',
        sort: true,
        clickableWithItem: () => true,
        linkStyle: () => ({'color': '#1565c0', 'text-decoration': 'underline', 'cursor': 'pointer'}),
      },
      {
        columnProperty: 'tripReference',
        columnHeader: 'Trip Reference (PNR)',
        sort: true,
        textLeft: true,
        clickable: true,
        linkEventFunction: (item: ITmtErpReportItem) => `/KtTrip/${item.tmtSalesOrderId}`,
        linkStyle: () => ({'color': '#1565c0', 'text-decoration': 'underline', 'cursor': 'pointer'}),
      },
      {
        columnProperty: 'changeType',
        columnHeader: 'Last change',
        sort: false,
      },
      {
        columnProperty: 'isSynced',
        columnHeader: 'Synced',
        sort: false,
        processData: (item: any) => item === true ? 'Yes' : item === false ? 'No' : '',
      },
      {
        columnProperty: 'mainClientName',
        columnHeader: 'Client Name',
        sort: true,
        textLeft: true,
        extraWidth: true,
      },
      {
        columnProperty: 'erpOrderNumber',
        columnHeader: 'ERP Sales Order',
        sort: false,
        textLeft: true,
      },
      {
        columnProperty: 'retryCount',
        columnHeader: 'Retries',
        sort: false,
      },
      {
        columnProperty: 'dateCreated',
        columnHeader: 'TMT Created',
        sort: true,
        processData: (item) => this.datePipe.transform(item, 'M/dd/yyyy HH:mm') ?? '',
      },
      {
        columnProperty: 'erpMessageTimestamp',
        columnHeader: 'Sent to ERP',
        sort: true,
        processData: (item) => item ? (this.datePipe.transform(item, 'M/dd/yyyy HH:mm') ?? '') : '',
      },
      {
        columnProperty: 'erpResponseDate',
        columnHeader: 'ERP Response',
        sort: false,
        processData: (item) => item ? (this.datePipe.transform(item, 'M/dd/yyyy HH:mm') ?? '') : '',
      },
      {
        columnProperty: 'erpResult',
        columnHeader: 'ERP Result',
        sort: false,
      },
      {
        columnProperty: 'errorMessage',
        columnHeader: 'Queue Error',
        sort: false,
        processData: (item) => item ? 'View Error' : '',
        linkStyle: () => ({'color': '#d32f2f', 'text-decoration': 'underline', 'cursor': 'pointer'}),
        clickableWithItem: (item) => !!item.errorMessage,
      },
      {
        columnProperty: 'errorDescription',
        columnHeader: 'ERP Error',
        sort: false,
        processData: (item) => item ? 'View Error' : '',
        linkStyle: () => ({'color': '#d32f2f', 'text-decoration': 'underline', 'cursor': 'pointer'}),
        clickableWithItem: (item) => !!item.errorDescription,
      },
    ],
    gridGetService: this.gridService,
  }));

  constructor() {
    effect(() => {
      const filters = this.filterService.filtersValue();
      if (!filters.ReloadGrid) return;
      this.gridRef().getItems();
    });
  }

  protected handleColumnClick(event: GridEventOutput<ITmtErpReportItem>): void {
    if (event.column === 'tmtSalesOrderId') {
      this.dialog.open(TmtErpTimelineDialogComponent, {
        width: '1200px',
        maxWidth: '96vw',
        data: {
          salesOrderId: event.item.tmtSalesOrderId,
          tripReference: event.item.tripReference,
        },
      });
      return;
    }
    if (event.column === 'tripReference') {
      this.router.navigate(['/KtTrip', event.item.tmtSalesOrderId]);
      return;
    }
    if (event.column === 'errorMessage' && event.item.errorMessage) {
      this.dialog.open(AdxErpPayloadDialogComponent, {
        width: '800px',
        maxWidth: '95vw',
        data: {title: 'Queue Error', content: event.item.errorMessage},
      });
    }
    if (event.column === 'errorDescription' && event.item.errorDescription) {
      this.dialog.open(AdxErpPayloadDialogComponent, {
        width: '800px',
        maxWidth: '95vw',
        data: {title: 'ERP Error', content: event.item.errorDescription},
      });
    }
  }

  protected openManualUpdateDialog(item: ITmtErpReportItem): void {
    if (!environment.enablePlayground) return;

    this.dialog.open(TmtErpManualUpdateDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      data: {
        salesOrderId: item.tmtSalesOrderId,
        tripReference: item.tripReference,
        existingResponse: item.erpResponseId ? {
          erpResponseId: item.erpResponseId,
          erpMessageId: item.erpMessageId,
          erpOrderNumber: item.erpOrderNumber,
          erpMessageTimestamp: item.erpMessageTimestamp,
          result: item.erpResult,
          errorDescription: item.errorDescription,
        } : null,
      },
    }).afterClosed().subscribe((result?: {updated: boolean}) => {
      if (result?.updated) {
        this.gridRef().getItems();
      }
    });
  }
}
